import { Container, Sprite, Texture } from 'pixi.js-legacy';
import {
  TILE_SIZE,
  GRID_OFFSET_X,
  GRID_OFFSET_Y,
  TILE_PADDING,
} from './grid-renderer';

interface Fragment {
  sprite: Sprite;
  velocityX: number;
  velocityY: number;
  rotation: number;
  life: number;
}

/**
 * Manages particle animations for tile destruction
 */
export class ParticleAnimator {
  private container: Container;
  private fragments: Fragment[] = [];
  private isAnimating: boolean = false;

  constructor(container: Container) {
    this.container = container;
  }

  /**
   * Create crumbling animation for tiles
   */
  createCrumbleEffect(
    positions: Array<{ x: number; y: number }>,
    texturePath: string
  ): void {
    this.isAnimating = true;

    for (const pos of positions) {
      const screenX = GRID_OFFSET_X + pos.x * (TILE_SIZE + TILE_PADDING);
      const screenY = GRID_OFFSET_Y + pos.y * (TILE_SIZE + TILE_PADDING);

      // Create 4 fragments per tile (2x2 grid)
      for (let fy = 0; fy < 2; fy++) {
        for (let fx = 0; fx < 2; fx++) {
          const fragment = this.createFragment(
            screenX,
            screenY,
            fx,
            fy,
            texturePath
          );
          this.fragments.push(fragment);
          this.container.addChild(fragment.sprite);
        }
      }
    }
  }

  /**
   * Create a single fragment
   */
  private createFragment(
    tileX: number,
    tileY: number,
    fragmentX: number,
    fragmentY: number,
    texturePath: string
  ): Fragment {
    const sprite = new Sprite(Texture.from(texturePath));

    // Each fragment is 1/2 the tile size
    const fragmentSize = TILE_SIZE / 2;
    const scale = fragmentSize / 64; // Original texture is 64px
    sprite.scale.set(scale, scale);

    // Position fragment at its quadrant of the tile
    sprite.x = tileX + fragmentX * fragmentSize;
    sprite.y = tileY + fragmentY * fragmentSize;

    // Set anchor to center for better rotation
    sprite.anchor.set(0.5, 0.5);
    sprite.x += fragmentSize / 2;
    sprite.y += fragmentSize / 2;

    // Random velocities for scatter effect
    const baseVelocityX = (fragmentX - 0.5) * 2; // Spread left/right based on position
    const baseVelocityY = (fragmentY - 0.5) * 2 - 2; // Initial upward velocity

    return {
      sprite,
      velocityX: baseVelocityX + (Math.random() - 0.5) * 2,
      velocityY: baseVelocityY + (Math.random() - 0.5) * 2,
      rotation: (Math.random() - 0.5) * 0.2,
      life: 1.0,
    };
  }

  /**
   * Update all active fragments
   */
  update(deltaTime: number): void {
    if (!this.isAnimating) return;

    const gravity = 0.3 * deltaTime;
    const fadeSpeed = 0.02 * deltaTime;

    for (let i = this.fragments.length - 1; i >= 0; i--) {
      const fragment = this.fragments[i];

      // Apply physics
      fragment.velocityY += gravity;
      fragment.sprite.x += fragment.velocityX * deltaTime;
      fragment.sprite.y += fragment.velocityY * deltaTime;
      fragment.sprite.rotation += fragment.rotation * deltaTime;

      // Fade out
      fragment.life -= fadeSpeed;
      fragment.sprite.alpha = fragment.life;

      // Remove dead fragments
      if (fragment.life <= 0) {
        this.container.removeChild(fragment.sprite);
        fragment.sprite.destroy();
        this.fragments.splice(i, 1);
      }
    }

    // Check if animation is complete
    if (this.fragments.length === 0) {
      this.isAnimating = false;
    }
  }

  /**
   * Check if any animations are currently running
   */
  isRunning(): boolean {
    return this.isAnimating;
  }

  /**
   * Clear all active fragments (for restart/undo)
   */
  clear(): void {
    for (const fragment of this.fragments) {
      this.container.removeChild(fragment.sprite);
      fragment.sprite.destroy();
    }
    this.fragments = [];
    this.isAnimating = false;
  }
}
